import React, { useState, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ClipboardCheck, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password,
      });
      setDone(true);
      toast.success(res.data?.message || 'Password updated');
      setTimeout(() => navigate('/login'), 2500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not reset password.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center mb-4 shadow-lg">
            <ClipboardCheck className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-800 tracking-tight">QA BUILDER PRO</h1>
        </div>

        <Card className="border-slate-200 shadow-xl bg-white">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="font-heading text-2xl tracking-tight" data-testid="reset-password-title">CHOOSE A NEW PASSWORD</CardTitle>
            <CardDescription className="text-slate-500">
              Enter a new password for your QA Builder Pro account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!token ? (
              <div
                className="flex flex-col items-center text-center py-6 space-y-4"
                data-testid="reset-password-missing-token"
              >
                <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 mb-1">Invalid reset link</h3>
                  <p className="text-sm text-slate-500">
                    This page needs a valid reset token. Please request a new password reset email.
                  </p>
                </div>
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary font-medium hover:text-primary/80"
                  data-testid="request-new-link"
                >
                  Request a new link
                </Link>
              </div>
            ) : done ? (
              <div
                className="flex flex-col items-center text-center py-4 space-y-4"
                data-testid="reset-password-success"
              >
                <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 mb-1">Password updated</h3>
                  <p className="text-sm text-slate-500">
                    You can now sign in with your new password. Redirecting…
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password" className="text-slate-700 font-medium">New password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    maxLength={128}
                    className="h-11 border-slate-200 focus:border-primary focus:ring-primary"
                    data-testid="reset-password-new-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-slate-700 font-medium">Confirm password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="Re-enter the new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    maxLength={128}
                    className="h-11 border-slate-200 focus:border-primary focus:ring-primary"
                    data-testid="reset-password-confirm-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 bg-primary hover:bg-primary/90 font-semibold shadow-md hover:shadow-lg transition-all active:scale-[0.98]"
                  disabled={isLoading || !password || !confirmPassword}
                  data-testid="reset-password-submit-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating password...
                    </>
                  ) : (
                    'Update password'
                  )}
                </Button>
                <Link
                  to="/login"
                  className="block text-center text-sm text-slate-500 hover:text-slate-700 pt-2"
                  data-testid="back-to-login-link"
                >
                  Back to sign in
                </Link>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 mt-6">© 2025 QA Builder Pro</p>
      </div>
    </div>
  );
}
